import { Link } from 'react-router-dom'
import { SocialLinks } from '@/components/CommunitySections'
import { useLanguage } from '@/localization'
import type { CommunityData } from '@/useCommunity'

// Anchor links use `/#id` so they work from any route (home scroll or /conduct).
// `Conduct` points to its own dedicated page.
const navLinks: { label: string; href?: string; to?: string }[] = [
  { label: 'Games', href: '/#games' },
  { label: 'Community', href: '/#community' },
  { label: 'Discord', href: '/#discord' },
  { label: 'Conduct', to: '/conduct' },
  { label: 'Team', href: '/#team' },
  { label: 'News', href: '/#news' },
  { label: 'Contact', href: '/#contact' },
]

// Platform-wide legal docs (canonical home for every Uliunai game site).
const legalLinks: { label: string; to: string }[] = [
  { label: 'Privacy Policy', to: '/privacy' },
  { label: 'Terms of Service', to: '/terms' },
]

const navLinkClass =
  'font-rajdhani text-sm font-semibold uppercase tracking-wider text-zinc-400 transition-colors hover:text-uliunai-red'

export default function Footer({ social }: { social: CommunityData['social'] }) {
  const year = new Date().getFullYear()
  const { t } = useLanguage()

  return (
    <footer className="border-t-2 border-uliunai-red/40 bg-ink-deep px-6 py-12 text-zinc-300">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-8 sm:flex-row sm:justify-between">
        {/* Wordmark + copyright */}
        <div className="flex items-center gap-3 text-center sm:text-left">
          <img src="/hub-logo.png" alt="" className="h-10 w-10 object-contain" />
          <div>
            <span className="font-orbitron text-xl font-black tracking-tight text-white">
              ULIUNAI
            </span>
            <p className="mt-0.5 font-rajdhani text-sm text-zinc-500">
              {t('One community. Every battlefield.')}
            </p>
          </div>
        </div>

        {/* Nav anchors */}
        <nav aria-label={t('Footer')} className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          {navLinks.map((l) =>
            l.to ? (
              <Link key={l.label} to={l.to} className={navLinkClass}>
                {t(l.label)}
              </Link>
            ) : (
              <a key={l.label} href={l.href} className={navLinkClass}>
                {t(l.label)}
              </a>
            ),
          )}
        </nav>

        {/* Social links */}
        <div className="flex items-center gap-3">
          <SocialLinks social={social} variant="dark" />
        </div>
      </div>

      {/* Legal docs — canonical platform-wide Privacy / Terms */}
      <nav
        aria-label={t('Legal')}
        className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-zinc-800 pt-8"
      >
        {legalLinks.map((l) => (
          <Link key={l.label} to={l.to} className={navLinkClass}>
            {t(l.label)}
          </Link>
        ))}
        <Link to="/conduct" className={navLinkClass}>
          {t('Code of Conduct')}
        </Link>
      </nav>

      <p className="mt-8 text-center font-rajdhani text-xs text-zinc-600">
        {t('Copyright')} {year} Uliunai.com. {t('All rights reserved.')} {t('Designed by')}{' '}
        <a
          href="https://litenweb.com"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-zinc-400 transition-colors hover:text-uliunai-red"
        >
          Litenweb
        </a>
        .
      </p>
    </footer>
  )
}
